import { useMutation } from "@apollo/client";
import { HStack, SystemStyleObject, Text } from "@chakra-ui/react";
import {
  AsyncCreatableSelect,
  AsyncSelect,
  MultiValue,
  PropsValue,
  SingleValue,
} from "chakra-react-select";
import * as EmailValidator from "email-validator";
import {
  InternalAdminCreateEmailUserMutation,
  InternalAdminCreateEmailUserMutationVariables,
  InternalAdminUserFragment,
  InternalAdminUserListQuery,
  InternalAdminUserListQueryVariables,
  UserRoleType,
} from "gql/graphql";
import { IIsNew } from "gql/isNew";
import debounce from "lodash/debounce";
import { UserEmailCircleIcon } from "modules/icons";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import colors from "app/colors";
import { getUserFullName, isMultiValue } from "modules/utils/Functions";
import useBurstQuery from "modules/hooks/use-burst-query";
import useQueryString from "modules/hooks/use-query-string";
import INTERNAL_ADMIN_CREATE_EMAIL_USER_MUTATION from "../_networking/internal-admin-create-email-user";
import INTERNAL_ADMIN_USER_LIST_QUERY from "../_networking/internal-admin-user-list";

interface ISelectInternalAdminUserFragment
  extends InternalAdminUserFragment,
    IIsNew {}

interface IProps {
  excludeUserIds?: Array<string>; //users who were already selected and should be excluded
  onChange: (users: InternalAdminUserFragment[]) => void;
  allowCreation: boolean;
  defaultValue?: InternalAdminUserFragment[];
  isMultiSelect: boolean;
  isDisabled?: boolean;
}

const InternalAdminEmailUserSelector = ({
  excludeUserIds,
  onChange,
  allowCreation,
  defaultValue,
  isMultiSelect,
  isDisabled = false,
}: IProps) => {
  const { qsOrganizationId } = useQueryString();
  const [value, setValue] =
    useState<PropsValue<ISelectInternalAdminUserFragment>>();

  const [internalAdminUserListQuery] = useBurstQuery<
    InternalAdminUserListQuery,
    InternalAdminUserListQueryVariables
  >(INTERNAL_ADMIN_USER_LIST_QUERY);

  const [internalAdminCreateUserEmailMutationAPI] = useMutation<
    InternalAdminCreateEmailUserMutation,
    InternalAdminCreateEmailUserMutationVariables
  >(INTERNAL_ADMIN_CREATE_EMAIL_USER_MUTATION);

  const getISelectInternalAdminUserFragments = useCallback(
    (users?: InternalAdminUserFragment[]) =>
      users
        ? users.map(
            (user): ISelectInternalAdminUserFragment => ({
              ...user,
              __isNew__: false,
            }),
          )
        : [],
    [],
  );

  const initialValues = useMemo(
    () => getISelectInternalAdminUserFragments(defaultValue),
    [defaultValue, getISelectInternalAdminUserFragments],
  );

  useEffect(() => {
    setValue(initialValues);
  }, [initialValues]);

  const _onChange = useCallback(
    (
      users:
        | SingleValue<ISelectInternalAdminUserFragment>
        | MultiValue<ISelectInternalAdminUserFragment>,
    ) => {
      setValue(users);
      if (users === null) {
        onChange([]);
        return;
      }
      if (isMultiValue(users)) {
        onChange([...users]);
      } else {
        onChange([
          {
            ...users,
          },
        ]);
      }
    },
    [onChange],
  );

  const onItemCreated = useCallback(
    async (email: string) => {
      if (!qsOrganizationId) return;
      const response = await internalAdminCreateUserEmailMutationAPI({
        variables: {
          organizationId: qsOrganizationId,
          input: {
            firstName: "",
            lastName: "",
            email,
          },
        },
      });
      const user = response.data?.internalAdminCreateEmailUser;
      if (user) {
        setValue((previous) => {
          const userWithIsNew: ISelectInternalAdminUserFragment = {
            ...user,
            __isNew__: true,
          };
          if (isMultiValue(previous)) {
            _onChange([...previous, userWithIsNew]);
            return [...previous, userWithIsNew];
          }
          _onChange(userWithIsNew);
          return userWithIsNew;
        });
      }
    },
    [internalAdminCreateUserEmailMutationAPI, _onChange, qsOrganizationId],
  );

  const _loadOptions = useCallback(
    (
      inputValue: string,
      callback: (options: ISelectInternalAdminUserFragment[]) => void,
    ) => {
      if (!qsOrganizationId) return;
      internalAdminUserListQuery({
        variables: {
          organizationId: qsOrganizationId,
          input: {
            limit: 25,
            offset: 0,
            filter: inputValue,
            active: true,
            roleTypeList: [UserRoleType.Email],
          },
        },
      }).then((response) => {
        callback(
          getISelectInternalAdminUserFragments(
            response.data.internalAdminUserList.filter(
              (obj) => !excludeUserIds?.includes(obj.id),
            ),
          ),
        );
      });
    },
    [
      excludeUserIds,
      getISelectInternalAdminUserFragments,
      internalAdminUserListQuery,
      qsOrganizationId,
    ],
  );

  const loadOptions = debounce(_loadOptions, 300);

  const formatOptionLabel = useCallback(
    (emailUser: ISelectInternalAdminUserFragment) => (
      <HStack>
        <UserEmailCircleIcon boxSize={5} fill={colors.white} />
        <Text fontWeight={700} color={colors.blueGreen} fontSize={14}>
          {emailUser.email}
        </Text>
      </HStack>
    ),
    [],
  );

  const getOptionLabel = useCallback(
    (user: ISelectInternalAdminUserFragment) =>
      `${user.email} ${getUserFullName(user)}`,
    [],
  );

  const getOptionValue = useCallback(
    (user: InternalAdminUserFragment) => user.id,
    [],
  );

  const getNewOptionData = useCallback(
    (inputValue: string): ISelectInternalAdminUserFragment => ({
      __isNew__: true,
      email: inputValue,
      id: "",
      organizationId: "",
      createTime: undefined,
      firstName: "",
      lastName: "",
      active: false,
      starredIdList: [],
      roleType: UserRoleType.Admin,
      tagIdList: [],
      emailUserIdList: [],
      isInternalAdmin: false,
      userPreferenceList: [],
      distributionGroupList: [],
      isHideEmail: false,
      isHidePhone: false,
      isAllDistributionGroups: false,
      userAttributeIdList: [],
    }),
    [],
  );

  const isValidNewOption = useCallback(
    (inputValue: string) => EmailValidator.validate(inputValue),
    [],
  );

  if (allowCreation) {
    return (
      <AsyncCreatableSelect
        loadOptions={loadOptions}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
        placeholder="Click to add email addresses"
        chakraStyles={{
          placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
            ...provided,
            fontStyle: "italic",
            color: colors.placeholderText,
          }),
          container: (provided: SystemStyleObject): SystemStyleObject => ({
            ...provided,
            width: "full",
          }),
        }}
        isMulti={isMultiSelect}
        defaultOptions
        formatOptionLabel={formatOptionLabel}
        getOptionValue={getOptionValue}
        getOptionLabel={getOptionLabel}
        onChange={_onChange}
        menuPlacement="bottom"
        isClearable
        value={value}
        isValidNewOption={isValidNewOption}
        onCreateOption={onItemCreated}
        getNewOptionData={getNewOptionData}
        createOptionPosition="first"
        isDisabled={isDisabled}
      />
    );
  }

  return (
    <AsyncSelect
      loadOptions={loadOptions}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      placeholder="Click to add email addresses"
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
        }),
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "full",
        }),
      }}
      isMulti={isMultiSelect}
      defaultOptions
      value={value}
      formatOptionLabel={formatOptionLabel}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      onChange={_onChange}
      menuPlacement="bottom"
      isClearable
      isDisabled={isDisabled}
    />
  );
};

export default memo(InternalAdminEmailUserSelector);
